import { useState } from "react";
import toast from "react-hot-toast";
import { CheckSquare, Plus, AlertTriangle } from "lucide-react";
import { useTasks, useUpdateTask, TASK_STATUS_LABEL, TASK_PRIORITY_LABEL, TaskStatus, isOverdue } from "@/api/tasks";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateTaskModal from "@/pages/tasks/CreateTaskModal";

const STATUS_STYLE: Record<TaskStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PROCESO: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  BLOQUEADA: "bg-red-100 text-red-700",
  APROBADA: "bg-teal-100 text-teal-700",
  FINALIZADA: "bg-green-100 text-green-700",
  CANCELADA: "bg-gray-100 text-gray-400",
};

const STATUS_OPTIONS = Object.keys(TASK_STATUS_LABEL) as TaskStatus[];

export default function TasksSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useTasks({ clientId, pageSize: 50 });
  const updateTask = useUpdateTask();
  const [showCreate, setShowCreate] = useState(false);

  const handleStatusChange = async (id: string, status: TaskStatus) => {
    try {
      await updateTask.mutateAsync({ id, status });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Tareas</h2>
        {hasPermission("tasks.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nueva tarea
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando tareas...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene tareas registradas.</p>
        )}
        {data?.data.map((task) => (
          <div key={task.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <CheckSquare size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{task.title}</p>
                <p className="text-xs text-gray-500">
                  {TASK_PRIORITY_LABEL[task.priority]}
                  {task.dueDate && (
                    <span className={isOverdue(task) ? "ml-1 inline-flex items-center gap-1 text-red-600" : "ml-1"}>
                      {isOverdue(task) && <AlertTriangle size={10} className="inline" />} · Vence {task.dueDate}
                    </span>
                  )}
                  {task.assignedTo && <span className="ml-1">· {task.assignedTo.fullName}</span>}
                </p>
              </div>
            </div>
            {hasPermission("tasks.update") ? (
              <select
                className={`shrink-0 rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[task.status]}`}
                value={task.status}
                onChange={(e) => handleStatusChange(task.id, e.target.value as TaskStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {TASK_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[task.status]}`}>
                {TASK_STATUS_LABEL[task.status]}
              </span>
            )}
          </div>
        ))}
      </div>

      {showCreate && <CreateTaskModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
